// Photo gallery for a recovery job.
//
// Images are stored in Firebase Storage under
//   companies/{companyId}/images/{jobId}/{imageId}
// and the download URLs are persisted on the job document (imageUrls).
//
// Per-image evidence metadata (type + GPS + timestamp) lives on the job doc in
// `typedImages` (JobImageEvidence[]). We match each rendered URL back to its
// evidence record (by URL, falling back to storage path) to surface location.

import { createContext, useContext, useEffect, useMemo, useRef, useState, type FormEvent, type MouseEventHandler, type ReactNode } from 'react';
import { getBlob, ref } from 'firebase/storage';
import { Check, Download, MapPin, RefreshCw, Tag, Trash2, X } from 'lucide-react';
import { createImageLoader, type ImageLoader } from '../lib/imageLoader';
import { withReadTimeout } from '../lib/readLoading';
import JSZip from 'jszip';
import { evidenceBucketUrl, storage } from '../lib/firebase';
import {
  evidenceCorrectionTypes, evidenceStoragePath, galleryGroups, galleryImages,
  runEvidenceActions, type GalleryImage,
} from '../lib/evidence';
import { formatCoords, formatTimestamp } from '../lib/format';
import { imageHasValidLocation, type Job, type JobImageEvidence } from '../types/models';
import { excludeJobImage, reclassifyJobImage } from '../lib/jobsBackend';
import { Alert } from './ui/Alert';
import { Field, Select, TextArea } from './ui/Field';
import type { WebImageType } from '../lib/imageUpload';
import { EmptyState } from './EmptyState';
import { Button } from './ui/Button';
import { Modal } from './ui/Modal';
import { PhotoTypeTag } from './JobTags';

interface Props {
  companyId: string;
  job: Job;
  canCorrectEvidence?: boolean;
  /** Rendered under the gallery's own header — the uploader belongs with the evidence. */
  uploader?: ReactNode;
}

const EVIDENCE_BUCKET = evidenceBucketUrl.replace(/^gs:\/\//, '');
// Old references name the former bucket, but all reads still target authenticated EU storage.
const LEGACY_BUCKET = storage.app.options.storageBucket;
const ImageLoaderContext = createContext<ImageLoader | null>(null);

function imageKey(image: GalleryImage) {
  return image.evidence?.imageId || image.evidence?.storagePath || image.url;
}

function locationCoords(e: JobImageEvidence): { latitude: number; longitude: number } | null {
  if (!imageHasValidLocation(e)) return null;
  return { latitude: Number(e.latitude), longitude: Number(e.longitude) };
}

function googleMapsUrl(e: JobImageEvidence): string | null {
  const coords = locationCoords(e);
  if (!coords) return null;
  return `https://www.google.com/maps/search/?api=1&query=${coords.latitude},${coords.longitude}`;
}

function googleMapsEmbedUrl(e: JobImageEvidence): string | null {
  const coords = locationCoords(e);
  if (!coords) return null;
  return `https://www.google.com/maps?q=${encodeURIComponent(`${coords.latitude},${coords.longitude}`)}&z=16&output=embed`;
}

function openLocationInBrowserMap(e: JobImageEvidence) {
  const url = googleMapsUrl(e);
  if (!url) return;
  window.open(
    url,
    '_blank',
    'noopener,noreferrer'
  );
}

export function PhotoGallery({ companyId, job, canCorrectEvidence = false, uploader }: Props) {
  const imageLoader = useMemo(() => createImageLoader(
    (path) => withReadTimeout(getBlob(ref(storage, path), 20 * 1024 * 1024), 20_000)
  ), [companyId, job.id]);
  useEffect(() => () => imageLoader.clear(), [imageLoader]);
  const [imageAttempt, setImageAttempt] = useState(0);
  const [lightbox, setLightbox] = useState<string | null>(null);
  const [downloading, setDownloading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [selecting, setSelecting] = useState(false);
  const [selectedImageIds, setSelectedImageIds] = useState<Set<string>>(
    () => new Set()
  );
  const [action, setAction] = useState<
    { kind: 'exclude' | 'reclassify'; images: GalleryImage[] } | null
  >(null);

  const images = useMemo(
    () => galleryImages(job, EVIDENCE_BUCKET, LEGACY_BUCKET),
    [job.imageSessions, job.imageUrls, job.typedImages]
  );
  const lightboxIndex = lightbox === null ? -1 : images.findIndex((image) => imageKey(image) === lightbox);

  const locatedCount = useMemo(
    () => images.filter((image) => imageHasValidLocation(image.evidence)).length,
    [images]
  );
  const correctableCount = useMemo(
    () => images.filter((image) => !!image.evidence?.imageId).length,
    [images]
  );
  const selectedImages = useMemo(
    () =>
      images.filter((image) => {
        const imageId = image.evidence?.imageId;
        return !!imageId && selectedImageIds.has(imageId);
      }),
    [images, selectedImageIds]
  );
  const imageGroups = useMemo(() => galleryGroups(images), [images]);

  useEffect(() => {
    const available = new Set(
      images
        .map((image) => image.evidence?.imageId)
        .filter((imageId): imageId is string => !!imageId)
    );
    setSelectedImageIds((current) => {
      const next = new Set([...current].filter((imageId) => available.has(imageId)));
      return next.size === current.size ? current : next;
    });
  }, [images]);

  function toggleImageSelection(image: GalleryImage) {
    const imageId = image.evidence?.imageId;
    if (!imageId) return;
    setSelectedImageIds((current) => {
      const next = new Set(current);
      if (next.has(imageId)) next.delete(imageId);
      else next.add(imageId);
      return next;
    });
  }

  function stopSelecting() {
    setSelecting(false);
    setSelectedImageIds(new Set());
  }

  function toggleGroupSelection(entries: Array<{ image: GalleryImage }>) {
    const imageIds = entries
      .map(({ image }) => image.evidence?.imageId)
      .filter((imageId): imageId is string => !!imageId);
    if (imageIds.length === 0) return;
    setSelectedImageIds((current) => {
      const next = new Set(current);
      const allSelected = imageIds.every((imageId) => next.has(imageId));
      for (const imageId of imageIds) {
        if (allSelected) next.delete(imageId);
        else next.add(imageId);
      }
      return next;
    });
  }

  async function downloadZip() {
    setDownloading(true);
    setError(null);
    try {
      const zip = new JSZip();
      const folder = zip.folder(job.registrationNumber || job.id) ?? zip;
      await Promise.all(
        images.map(async (image, idx) => {
          const blob = await loadAuthenticatedImageBlob(imageLoader, image.url, image.evidence?.storagePath);
          folder.file(filenameFromUrl(image.url, idx), blob);
        })
      );
      const out = await zip.generateAsync({ type: 'blob' });
      const a = document.createElement('a');
      a.href = URL.createObjectURL(out);
      a.download = `${job.registrationNumber || job.id}-photos.zip`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(a.href);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Failed to download photos. Try again.');
    } finally {
      setDownloading(false);
    }
  }

  // The gallery owns its header, so the count and the controls that act on these
  // photographs sit together rather than split across the page's card.
  const header = (
    <div className="flex flex-wrap items-center justify-between gap-2 px-5 pt-4">
      <h2 className="text-[16.5px] font-semibold tracking-[-0.01em] text-ink">
        Evidence <span className="font-mono text-[15px] font-medium text-ink3">({images.length})</span>
      </h2>
      <div className="flex flex-wrap items-center gap-1.5">
        {canCorrectEvidence && correctableCount > 0 && selecting && (
          <>
            <span className="font-mono text-[13px] text-ink2">{selectedImages.length} selected</span>
            <Button type="button" variant="secondary" size="sm" icon={Tag}
              disabled={selectedImages.length === 0}
              onClick={() => setAction({ kind: 'reclassify', images: selectedImages })}>
              Change heading
            </Button>
            <Button type="button" variant="danger-outline" size="sm" icon={Trash2}
              disabled={selectedImages.length === 0}
              onClick={() => setAction({ kind: 'exclude', images: selectedImages })}>
              Delete
            </Button>
            <Button type="button" variant="ghost" size="sm" icon={X} onClick={stopSelecting}>Done</Button>
          </>
        )}
        <Button type="button" variant="ghost" size="sm" icon={RefreshCw} onClick={() => setImageAttempt((n) => n + 1)}>
          Reload
        </Button>
        <Button type="button" variant="secondary" size="sm" icon={Download} loading={downloading} onClick={downloadZip}>
          {downloading ? 'Preparing…' : 'Download'}
        </Button>
      </div>
    </div>
  );

  if (images.length === 0) {
    return (
      <>
        {header}
        <div className="space-y-4 p-5 pt-4">
          {uploader}
          <EmptyState title="No photographs yet"
            description="Evidence added here is verified by the server before it joins the job." />
        </div>
      </>
    );
  }

  return (
    <ImageLoaderContext.Provider value={imageLoader}>
    {header}
    <div className="p-5 pt-4">
      {uploader && <div className="mb-4">{uploader}</div>}
      <p className="mb-3 text-[13px] text-ink3">
        {locatedCount > 0
          ? `${locatedCount} of ${images.length} photograph${images.length === 1 ? '' : 's'} carry a location — open one to see where it was taken.`
          : 'Open a photograph to see it full size. No location was recorded on these.'}
      </p>

      <div className="mb-3 space-y-2 empty:hidden">
        {error && <Alert tone="error">{error}</Alert>}
        {notice && <Alert tone="success">{notice}</Alert>}
      </div>

      <div className="space-y-5">
        {imageGroups.map((group) => {
          const selectableIds = group.entries
            .map(({ image }) => image.evidence?.imageId)
            .filter((imageId): imageId is string => !!imageId);
          const allSelected =
            selectableIds.length > 0 &&
            selectableIds.every((imageId) => selectedImageIds.has(imageId));

          return (
            <section key={group.type}>
              {/* The heading names the evidence and carries its count, as on the phone. */}
              <div className="mb-2.5 flex min-h-7 items-center gap-2">
                <h3><PhotoTypeTag type={group.type} label={group.label} /></h3>
                <span className="font-mono text-[12px] text-ink3">{group.entries.length}</span>
                {canCorrectEvidence && selecting && selectableIds.length > 0 && (
                  <button
                    type="button"
                    onClick={() => toggleGroupSelection(group.entries)}
                    className="ml-auto rounded-[8px] px-2 py-1 text-[14px] font-semibold text-accent hover:bg-accenttint"
                    aria-label={`${allSelected ? 'Deselect' : 'Select'} all ${group.label} photos`}
                  >
                    {allSelected ? 'Deselect all' : 'Select all'}
                  </button>
                )}
                {!selecting && canCorrectEvidence && selectableIds.length > 0 && (
                  <button
                    type="button"
                    onClick={() => {
                      setSelecting(true);
                      setError(null);
                      setNotice(null);
                    }}
                    className="ml-auto rounded-[8px] px-2 py-1 text-[14px] font-semibold text-accent hover:bg-accenttint"
                    aria-label={`Select ${group.label} photos`}
                  >
                    Select
                  </button>
                )}
              </div>
              <div className="evidence-photo-grid">
                {group.entries.map(({ image, index }) => {
                  const located = imageHasValidLocation(image.evidence);
                  const imageId = image.evidence?.imageId;
                  const selected = !!imageId && selectedImageIds.has(imageId);
                  return (
                    <button
                      key={imageKey(image)}
                      type="button"
                      aria-pressed={selecting ? selected : undefined}
                      onClick={() =>
                        selecting ? toggleImageSelection(image) : setLightbox(imageKey(image))
                      }
                      className={`group relative aspect-square overflow-hidden rounded-[10px] bg-sunken focus:outline-none focus-visible:ring-2 focus-visible:ring-accent ${
                        selected ? 'shadow-[inset_0_0_0_2.5px_var(--accent)]' : ''
                      } ${selecting && !imageId ? 'cursor-not-allowed opacity-55' : ''}`}
                    >
                      <AuthenticatedStorageImage
                        attempt={imageAttempt}
                        lazy
                        url={image.url}
                        storagePath={image.evidence?.thumbnailPath || image.evidence?.storagePath}
                        fallbackStoragePath={image.evidence?.storagePath}
                        alt={`${group.label} photo ${index + 1}`}
                        className="h-full w-full object-cover transition group-hover:scale-[1.02]"
                      />
                      {!selecting && <span className="absolute left-1.5 top-1.5 max-w-[calc(100%-0.75rem)]"><PhotoTypeTag type={group.type} label={group.label} overlay /></span>}
                      {located && (
                        <span className="absolute bottom-1.5 right-1.5 flex items-center gap-1 rounded-full bg-black/60 px-2 py-0.5 text-[10px] font-semibold text-white">
                          <MapPin size={11} /> GPS
                        </span>
                      )}
                      {selecting && imageId && (
                        <span
                          className={`absolute left-2 top-2 flex h-6 w-6 items-center justify-center rounded-full border-2 text-white shadow ${
                            selected
                              ? 'border-accent bg-accent'
                              : 'border-white/90 bg-black/35'
                          }`}
                        >
                          {selected && <Check size={14} strokeWidth={3} />}
                        </span>
                      )}
                    </button>
                  );
                })}
              </div>
            </section>
          );
        })}
      </div>

      {lightboxIndex >= 0 && (
        <Lightbox
          attempt={imageAttempt}
          image={images[lightboxIndex]}
          onClose={() => setLightbox(null)}
          onPrev={lightboxIndex > 0 ? () => setLightbox(imageKey(images[lightboxIndex - 1])) : undefined}
          onNext={lightboxIndex < images.length - 1 ? () => setLightbox(imageKey(images[lightboxIndex + 1])) : undefined}
          canCorrectEvidence={canCorrectEvidence && !!images[lightboxIndex].evidence?.imageId}
          onExclude={() => setAction({ kind: 'exclude', images: [images[lightboxIndex]] })}
          onReclassify={() => setAction({ kind: 'reclassify', images: [images[lightboxIndex]] })}
        />
      )}

      {canCorrectEvidence && action && action.images.length > 0 && (
        <EvidenceActionModal
          companyId={companyId}
          jobId={job.id}
          action={action.kind}
          courtesyCarRequired={job.courtesyCarRequired === true}
          evidence={action.images
            .map((image) => image.evidence)
            .filter((item): item is JobImageEvidence => !!item?.imageId)}
          onClose={() => setAction(null)}
          onDone={({ succeeded, total, failures }) => {
            if (action.kind === 'exclude') setLightbox(null);
            if (failures.length > 0) {
              setError(
                `${action.kind === 'exclude' ? 'Removed' : 'Changed'} ${succeeded} of ${total}. ${failures.length} failed: ${failures[0]}`
              );
            } else {
              setError(null);
              setNotice(
                action.kind === 'exclude'
                  ? `${succeeded} photo${succeeded === 1 ? '' : 's'} removed from this job with an audit record.`
                  : `${succeeded} photo type${succeeded === 1 ? '' : 's'} corrected with an audit record.`
              );
            }
            stopSelecting();
            setAction(null);
          }}
        />
      )}
    </div>
    </ImageLoaderContext.Provider>
  );
}

function filenameFromUrl(url: string, idx: number): string {
  try {
    const u = new URL(url);
    const last = decodeURIComponent(u.pathname.split('/').pop() || '');
    if (last && /\.(jpe?g|png|webp)$/i.test(last)) return last;
  } catch {
    /* ignore */
  }
  return `photo-${String(idx + 1).padStart(3, '0')}.jpg`;
}

function Lightbox({
  attempt,
  image,
  onClose,
  onPrev,
  onNext,
  canCorrectEvidence,
  onExclude,
  onReclassify,
}: {
  attempt: number;
  image: GalleryImage;
  onClose: () => void;
  onPrev?: () => void;
  onNext?: () => void;
  canCorrectEvidence: boolean;
  onExclude: () => void;
  onReclassify: () => void;
}) {
  const located = imageHasValidLocation(image.evidence);
  const mapPreviewUrl = located ? googleMapsEmbedUrl(image.evidence!) : null;
  return (
    <div className="fixed inset-0 z-30 flex items-center justify-center bg-black/80 p-4" onClick={onClose}>
      <button onClick={onClose} className="absolute right-4 top-4 rounded-full bg-white/10 px-3 py-1 text-sm text-white hover:bg-white/20">
        Close
      </button>
      {onPrev && (
        <button onClick={(e) => { e.stopPropagation(); onPrev(); }} className="absolute left-4 top-1/2 -translate-y-1/2 rounded-full bg-white/10 px-4 py-2 text-white hover:bg-white/20">
          ‹
        </button>
      )}
      <AuthenticatedStorageImage
        attempt={attempt}
        url={image.url}
        storagePath={image.evidence?.storagePath}
        alt=""
        onClick={(e) => e.stopPropagation()}
        className="max-h-full max-w-full rounded shadow-2xl"
      />
      {onNext && (
        <button onClick={(e) => { e.stopPropagation(); onNext(); }} className="absolute right-4 top-1/2 -translate-y-1/2 rounded-full bg-white/10 px-4 py-2 text-white hover:bg-white/20">
          ›
        </button>
      )}

      {mapPreviewUrl && (
        <div
          className="absolute inset-x-4 bottom-28 overflow-hidden rounded-xl border border-white/20 bg-black/45 shadow-2xl backdrop-blur sm:left-auto sm:right-4 sm:w-80"
          onClick={(e) => e.stopPropagation()}
        >
          <iframe
            title="Photo location map"
            src={mapPreviewUrl}
            className="h-32 w-full border-0 sm:h-44"
            loading="lazy"
            referrerPolicy="no-referrer-when-downgrade"
          />
        </div>
      )}

      {/* Provenance, under the photograph it belongs to. Capture time is the shutter,
          never the upload — they mean different things in a dispute, so they are
          labelled differently and never merged. */}
      <div
        className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/85 to-transparent px-4 pb-5 pt-14"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="mx-auto max-w-xl rounded-[12px] bg-black/55 p-4 backdrop-blur-sm">
          <ProvenanceRow label="Captured"
            value={image.evidence?.capturedAt ? formatTimestamp(image.evidence.capturedAt) : 'Not recorded'} />
          {image.evidence?.uploadedAt && (
            <ProvenanceRow label="Uploaded" value={formatTimestamp(image.evidence.uploadedAt)} />
          )}
          <ProvenanceRow
            label="Location"
            value={located ? formatCoords(image.evidence!.latitude, image.evidence!.longitude) : 'Not recorded'}
            action={located ? (
              <button onClick={() => openLocationInBrowserMap(image.evidence!)}
                className="inline-flex items-center gap-1 rounded-full bg-white/15 px-2.5 py-1 text-[12.5px] font-semibold hover:bg-white/25">
                <MapPin size={13} /> Map
              </button>
            ) : undefined}
          />

          {canCorrectEvidence && (
            <div className="mt-3 flex flex-wrap justify-end gap-2 border-t border-white/15 pt-3">
              <button type="button" onClick={onReclassify}
                className="inline-flex items-center gap-1.5 rounded-full bg-white/15 px-3 py-1.5 text-[13.5px] font-semibold text-white hover:bg-white/25">
                <Tag size={14} /> Change heading
              </button>
              <button type="button" onClick={onExclude}
                className="inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-[13.5px] font-semibold text-white shadow-[inset_0_0_0_1.5px_rgba(255,255,255,0.5)] hover:bg-white/15">
                <Trash2 size={14} /> Delete
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function ProvenanceRow({
  label, value, action, tone,
}: {
  label: string;
  value: string;
  action?: ReactNode;
  tone?: 'ok';
}) {
  return (
    <div className="flex min-h-8 items-center gap-3 border-t border-white/10 py-1.5 first:border-t-0 first:pt-0">
      <span className="text-[13.5px] text-white/70">{label}</span>
      <span className={`ml-auto min-w-0 flex-1 break-words text-right font-mono text-[13px] ${tone === 'ok' ? 'text-success-ondark' : 'text-white'}`}>
        {value}
      </span>
      {action}
    </div>
  );
}

function EvidenceActionModal({
  companyId,
  jobId,
  action,
  courtesyCarRequired,
  evidence,
  onClose,
  onDone,
}: {
  companyId: string;
  jobId: string;
  action: 'exclude' | 'reclassify';
  courtesyCarRequired: boolean;
  evidence: JobImageEvidence[];
  onClose: () => void;
  onDone: (result: {
    succeeded: number;
    total: number;
    failures: string[];
  }) => void;
}) {
  const [reason, setReason] = useState('');
  const types = evidenceCorrectionTypes(courtesyCarRequired);
  const [newType, setNewType] = useState<WebImageType>(() => {
    const current = evidence[0]?.type as WebImageType | null | undefined;
    return current && types.some((type) => type.value === current)
      ? current
      : 'additional';
  });
  const [saving, setSaving] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const [progress, setProgress] = useState({ completed: 0, total: 0 });
  const targetType = types.some((type) => type.value === newType) ? newType : 'additional';

  useEffect(() => {
    if (!courtesyCarRequired) setNewType((current) => current === 'courtesy' ? 'additional' : current);
  }, [courtesyCarRequired]);

  async function submit(event: FormEvent) {
    event.preventDefault();
    if (saving) return;
    const cleanReason = reason.trim();
    const targets = evidence.filter(
      (item): item is JobImageEvidence & { imageId: string } => !!item.imageId
    );
    if (targets.length === 0 || cleanReason.length < 3 || cleanReason.length > 500) return;
    const actionableTargets =
      action === 'reclassify'
        ? targets.filter((target) => targetType !== target.type)
        : targets;
    if (actionableTargets.length === 0) {
      setActionError('Choose a different evidence type.');
      return;
    }

    setSaving(true);
    setActionError(null);
    const result = await runEvidenceActions(actionableTargets, async (target) => {
      if (action === 'exclude') {
        await excludeJobImage({
          companyId,
          jobId,
          imageId: target.imageId,
          reason: cleanReason,
        });
      } else {
        await reclassifyJobImage({
          companyId,
          jobId,
          imageId: target.imageId,
          newType: targetType,
          reason: cleanReason,
        });
      }
    }, (completed, total) => setProgress({ completed, total }), action === 'reclassify' ? 3 : 1);
    setSaving(false);

    if (result.succeeded === 0 && result.failures.length > 0) {
      setActionError(result.failures[0]);
      return;
    }
    onDone(result);
  }

  const isExclude = action === 'exclude';
  return (
    <Modal
      open
      dismissible={!saving}
      onClose={onClose}
      title={
        isExclude
          ? `Delete ${evidence.length} photograph${evidence.length === 1 ? '' : 's'} from this job`
          : `Change type for ${evidence.length} photo${evidence.length === 1 ? '' : 's'}`
      }
    >
      <form onSubmit={submit} className="space-y-4">
        <p className="text-sm leading-relaxed text-ink2">
          {isExclude
            ? 'The selected photographs come off this job and out of its evidence packs. The original files and their records are kept and each removal is recorded against your name, so a mistake can still be accounted for.'
            : 'Only the filing labels change. The original photos, checksums, capture times and locations stay exactly the same, and every correction is audited.'}
        </p>
        {!isExclude && (
          <Field label="New heading">
            <Select value={targetType} disabled={saving}
              onChange={(event) => setNewType(event.target.value as WebImageType)}>
              {types.map((type) => (
                <option key={type.value} value={type.value}>{type.label}</option>
              ))}
            </Select>
          </Field>
        )}
        <Field label="Reason" hint="Recorded with your name against every photograph. 3–500 characters.">
          <TextArea
            value={reason}
            disabled={saving}
            onChange={(event) => setReason(event.target.value)}
            rows={3}
            maxLength={500}
            placeholder={isExclude ? 'For example: wrong vehicle' : 'For example: filed under the wrong heading'}
          />
        </Field>
        {saving && (
          <div className="space-y-2" role="status" aria-live="polite">
            <div className="flex items-center justify-between text-[14px] text-ink2">
              <span>{isExclude ? 'Deleting' : 'Reclassifying'}</span>
              <span className="font-mono">{progress.completed} / {progress.total}</span>
            </div>
            <div className="h-1.5 overflow-hidden rounded-full bg-sunken">
              <div className="h-full rounded-full bg-st-progress transition-[width] duration-[180ms] ease-out"
                style={{ width: `${(progress.completed / Math.max(progress.total, 1)) * 100}%` }} />
            </div>
          </div>
        )}
        {actionError && <Alert tone="error">{actionError}</Alert>}
        <div className="flex justify-end gap-2">
          <Button type="button" variant="secondary" disabled={saving} onClick={onClose}>
            Cancel
          </Button>
          <Button
            type="submit"
            variant={isExclude ? 'danger' : 'primary'}
            loading={saving}
            disabled={reason.trim().length < 3}
          >
            {isExclude ? 'Delete photograph' : 'Change heading'}
          </Button>
        </div>
      </form>
    </Modal>
  );
}

async function loadAuthenticatedImageBlob(loader: ImageLoader, url: string, knownStoragePath?: string | null, priority = false): Promise<Blob> {
  const storagePath = knownStoragePath || evidenceStoragePath(url, EVIDENCE_BUCKET, LEGACY_BUCKET);
  if (!storagePath) {
    throw new Error('This evidence file has no valid Storage path.');
  }
  // Authenticated SDK reads enforce tenant/job Storage rules. Never fall back
  // to the persisted token URL: that would bypass the denial that just occurred.
  return loader.load(storagePath, priority);
}

function AuthenticatedStorageImage({
  attempt,
  lazy = false,
  url,
  storagePath,
  fallbackStoragePath,
  alt,
  className,
  onClick,
}: {
  attempt: number;
  lazy?: boolean;
  url: string;
  storagePath?: string | null;
  fallbackStoragePath?: string | null;
  alt: string;
  className: string;
  onClick?: MouseEventHandler<HTMLImageElement>;
}) {
  const [objectUrl, setObjectUrl] = useState<string | null>(null);
  const [failure, setFailure] = useState<string | null>(null);
  const loader = useContext(ImageLoaderContext)!;
  const container = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(!lazy);

  useEffect(() => {
    if (visible || !container.current) return;
    const observer = new IntersectionObserver((entries) => {
      if (entries.some((entry) => entry.isIntersecting)) {
        setVisible(true);
        observer.disconnect();
      }
    }, { rootMargin: '400px' });
    observer.observe(container.current);
    return () => observer.disconnect();
  }, [visible]);

  useEffect(() => {
    if (!visible) return;
    let active = true;
    let createdUrl: string | null = null;
    setObjectUrl(null);
    setFailure(null);
    void loadAuthenticatedImageBlob(loader, url, storagePath, !lazy)
      .catch((error) => {
        if (
          fallbackStoragePath &&
          fallbackStoragePath !== storagePath &&
          isStorageObjectMissing(error)
        ) {
          return loadAuthenticatedImageBlob(loader, url, fallbackStoragePath, !lazy);
        }
        throw error;
      })
      .then((blob) => {
        if (!active) return;
        createdUrl = URL.createObjectURL(blob);
        setObjectUrl(createdUrl);
      })
      .catch((error: unknown) => {
        if (!active) return;
        const code = error && typeof error === 'object' && 'code' in error ? String(error.code) : '';
        setFailure(code === 'storage/unauthorized' ? 'Photo access denied' : code === 'storage/object-not-found' ? 'Photo file not found' : 'Photo could not load');
      });
    return () => {
      active = false;
      if (createdUrl) URL.revokeObjectURL(createdUrl);
    };
  }, [attempt, fallbackStoragePath, lazy, loader, storagePath, url, visible]);

  if (failure) {
    return <div className={`${className} flex items-center justify-center p-3 text-center text-sm text-ink2`} role="status">{failure}</div>;
  }
  if (!objectUrl) {
    return <div ref={container} className={`${className} animate-pulse bg-hover`} role="status" aria-label={`Loading ${alt || 'photo'}`} />;
  }
  return <img src={objectUrl} alt={alt} loading="lazy" onClick={onClick} className={className} />;
}

function isStorageObjectMissing(error: unknown): boolean {
  if (!error || typeof error !== 'object' || !('code' in error)) return false;
  return String((error as { code?: unknown }).code) === 'storage/object-not-found';
}
